import {
  ConfigurableListOption,
  DEFAULT_CLOUD_TYPES,
} from "resource-planner-model";
import { useSelector } from "react-redux";
import { AppState } from "../redux/AppTypes";

export interface TeamTypeOption {
  id: string;
  label: string;
}

interface TeamTypeSource {
  type?: string[];
}

const applicationConfigSelector = (state: AppState) =>
  state.applicationConfig?.item || {
    cloudTypes: DEFAULT_CLOUD_TYPES,
  };

const teamsSelector = (state: AppState): TeamTypeSource[] =>
  Object.values(state.teams?.items || {}) as TeamTypeSource[];

export function buildTeamTypeOptions(
  cloudTypes: ConfigurableListOption[] = DEFAULT_CLOUD_TYPES,
  teams: TeamTypeSource[] = [],
  extraTypeIds: string[] = []
): TeamTypeOption[] {
  const optionsById = new Map<string, TeamTypeOption>();
  const configuredById = new Map<string, ConfigurableListOption>();

  (cloudTypes || []).forEach((cloudType) => {
    if (cloudType.id) {
      configuredById.set(cloudType.id, cloudType);
    }
  });

  function addOption(id: string, label?: string): void {
    if (!id || optionsById.has(id)) {
      return;
    }
    optionsById.set(id, {
      id,
      label: label || id,
    });
  }

  (cloudTypes || [])
    .filter((cloudType) => cloudType.id && !cloudType.archived)
    .sort((left, right) => left.order - right.order)
    .forEach((cloudType) => addOption(cloudType.id, cloudType.label));

  teams.forEach((team) =>
    (team.type || []).forEach((typeId) => {
      const configured = configuredById.get(typeId);
      addOption(typeId, configured?.label);
    })
  );
  extraTypeIds.forEach((typeId) => {
    const configured = configuredById.get(typeId);
    addOption(typeId, configured?.label);
  });

  return Array.from(optionsById.values());
}

export function getTeamTypeLabel(
  options: TeamTypeOption[],
  typeId: string
): string {
  return options.find((option) => option.id === typeId)?.label || typeId;
}

export function useTeamTypes(extraTypeIds: string[] = []): TeamTypeOption[] {
  const config = useSelector(applicationConfigSelector);
  const teams = useSelector(teamsSelector);
  return buildTeamTypeOptions(config.cloudTypes, teams, extraTypeIds);
}
